import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import PhoneButton from "./PhoneButton";

const VIDEO_URL = "/video_trimmed.mp4";
const FALLBACK_IMAGE =
  "https://customer-assets.emergentagent.com/job_e8fd11b7-6a50-45dd-b264-41dac64b79ed/artifacts/37k8850m_DJI_20250303115431_0015_D.jpg";

export default function HeroSection() {
  const videoRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.playbackRate = 0.65;

    const playVideo = async () => {
      try {
        await video.play();
      } catch {
        setVideoFailed(true);
      }
    };

    if (video.readyState >= 2) {
      playVideo();
    } else {
      video.addEventListener("loadeddata", playVideo, { once: true });
    }

    video.addEventListener("error", () => setVideoFailed(true), { once: true });

    return () => {
      video.removeEventListener("loadeddata", playVideo);
    };
  }, []);

  return (
    <section data-testid="hero-section" className="video-hero">
      {/* Fallback image - always visible as base layer */}
      <img
        src={FALLBACK_IMAGE}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-30"
      />

      {/* Video on top */}
      {!videoFailed && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
      )}

      {/* Overlays - lighter for brightness */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/40 via-black/20 to-black/80" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/30 via-transparent to-black/30" />

      {/* Decorative corner marks */}
      <div className="absolute top-28 left-8 lg:left-16 z-20 w-16 h-16 border-l border-t border-gold/20" />
      <div className="absolute top-28 right-8 lg:right-16 z-20 w-16 h-16 border-r border-t border-gold/20" />
      <div className="absolute bottom-20 left-8 lg:left-16 z-20 w-16 h-16 border-l border-b border-gold/20" />
      <div className="absolute bottom-20 right-8 lg:right-16 z-20 w-16 h-16 border-r border-b border-gold/20" />

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="gold-line w-16 mb-8 animate-fade-in" />

        <h1
          className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4 animate-fade-in-up max-w-3xl leading-tight text-center"
        >
          Chauffeur Privé<br />
          <span className="text-gold">Haut de Gamme</span>
        </h1>

        <p
          className="font-accent italic text-gold/80 text-lg sm:text-xl lg:text-2xl tracking-[0.15em] mb-12 animate-fade-in-up animation-delay-200 max-w-xl"
        >
          Brest &middot; Finistère &middot; Bretagne
        </p>

        <div className="flex flex-col sm:flex-row gap-5 animate-fade-in-up animation-delay-400">
          <a
            href="#reservation"
            data-testid="hero-cta-booking"
            className="group relative bg-gold text-black font-semibold px-12 py-4 uppercase tracking-[0.25em] text-xs hover:bg-white transition-colors duration-500 overflow-hidden"
          >
            <span className="relative z-10">Réserver maintenant</span>
          </a>
          <PhoneButton
            className="border border-white/20 text-white/90 px-12 py-4 uppercase tracking-[0.25em] text-xs hover:border-gold hover:text-gold transition-all duration-500"
          >
            06 76 80 62 03
          </PhoneButton>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-white/30 text-[10px] uppercase tracking-[0.3em]">Découvrir</span>
          <ChevronDown size={18} className="text-gold/50 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
